import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { Horse } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import api, { apiBaseURL } from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx, smartPageSx } from '../../theme/smartStableTheme';

const COPY = {
  ar: {
    title: 'تفاصيل الخيل',
    subtitle: 'عرض فقط بدون إمكانية تعديل.',
    back: 'العودة للخيل',
    notFound: 'لم يتم العثور على الخيل ضمن حسابك',
    father: 'الأب',
    mother: 'الأم',
    breeder: 'المربي',
    gender: 'الجنس',
    birth: 'تاريخ الميلاد',
    rfid: 'رقم الشريحة',
    breed: 'السلالة',
    color: 'اللون',
    country: 'بلد المنشأ',
    location: 'السكن الحالي',
    registration: 'رقم التسجيل',
  },
  en: {
    title: 'Horse Details',
    subtitle: 'Read-only details for your horse.',
    back: 'Back to horses',
    notFound: 'This horse was not found in your account',
    father: 'Father',
    mother: 'Mother',
    breeder: 'Breeder',
    gender: 'Gender',
    birth: 'Date of birth',
    rfid: 'RFID number',
    breed: 'Breed',
    color: 'Color',
    country: 'Country of origin',
    location: 'Current accommodation',
    registration: 'Registration no.',
  },
};

const getImageSrc = (image) => {
  if (!image) return undefined;
  if (image.startsWith('data') || image.startsWith('http')) return image;
  return `${apiBaseURL}/uploads/${image}`;
};

const pickName = (en, ar, lang) => (lang === 'ar' ? ar || en || '-' : en || ar || '-');

const ClientHorseDetails = () => {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const ownerId = localStorage.getItem('owner_id');
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/owners/${ownerId}/horses-with-details`);
        if (alive) setHorses(Array.isArray(data) ? data : []);
      } catch {
        if (alive) setError(text.notFound);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [ownerId, text.notFound]);

  const horse = useMemo(
    () => horses.find((item) => Number(item.horse_id) === Number(horseId)),
    [horseId, horses]
  );

  const rows = useMemo(() => {
    if (!horse) return [];
    return [
      { icon: <BadgeIcon />, label: text.rfid, value: horse.rfid_chip_number || '-' },
      { icon: <CakeIcon />, label: text.birth, value: horse.date_of_birth ? String(horse.date_of_birth).slice(0, 10) : '-' },
      { icon: <Horse size={22} weight="fill" />, label: text.gender, value: horse.gender || '-' },
      { icon: <Horse size={22} weight="fill" />, label: text.father, value: pickName(horse.father_name, horse.father_name_arabic, lang) },
      { icon: <Horse size={22} weight="fill" />, label: text.mother, value: pickName(horse.mother_name, horse.mother_name_arabic, lang) },
      { icon: <Horse size={22} weight="fill" />, label: text.breeder, value: pickName(horse.breeder_name, horse.breeder_name_arabic, lang) },
      { icon: <Horse size={22} weight="fill" />, label: text.breed, value: pickName(horse.breed_english, horse.breed_arabic, lang) },
      { icon: <Horse size={22} weight="fill" />, label: text.color, value: pickName(horse.color_english, horse.color_arabic, lang) },
      { icon: <HomeWorkIcon />, label: text.location, value: [horse.stable_name, horse.room_name].filter(Boolean).join(' - ') || '-' },
      { icon: <BadgeIcon />, label: text.registration, value: horse.registration_number || '-' },
    ];
  }, [horse, lang, text]);

  if (loading) {
    return (
      <Box sx={{ ...smartPageSx, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !horse) {
    return (
      <Box sx={{ ...smartPageSx, px: 3, py: 3 }}>
        <Alert severity="warning">{error || text.notFound}</Alert>
      </Box>
    );
  }

  return (
    <Box dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ ...smartPageSx, px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2, md: 3 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={getImageSrc(horse.image)} sx={{ width: 84, height: 84, bgcolor: COLORS.goldSoft, color: COLORS.gold, fontWeight: 900 }}>
                {pickName(horse.name, horse.name_arabic, lang).charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  {pickName(horse.name, horse.name_arabic, lang)}
                </Typography>
                <Typography sx={{ color: COLORS.muted, mt: 0.4 }}>{text.subtitle}</Typography>
              </Box>
            </Stack>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/my-horses')} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {text.back}
            </Button>
          </Stack>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 2 }}>
          {rows.map((row) => (
            <Paper key={row.label} elevation={0} sx={{ ...smartCardSx, p: 2 }}>
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Avatar sx={{ bgcolor: COLORS.cardSoft, color: COLORS.gold }}>{row.icon}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ color: COLORS.text, fontWeight: 900, wordBreak: 'break-word' }}>
                    {row.value}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ClientHorseDetails;
